import { convertValue, round2decimals } from "../charts/utils";
import { IDataSet, IStatsRow } from "./interfaces";
import { AggregationType } from "./types";

export const dateToTimestamp = (date: Date | undefined) => {
  if (date !== undefined) {
    return date.getTime();
  }
};

export const minusSixHours = (timestamp: number) => {
  const newDate: Date = new Date(timestamp);
  newDate.setHours(newDate.getHours() - 6);
  return newDate;
};

export const minusSixHoursDate = (date: Date) => {
  const newDate: Date = new Date(date);
  newDate.setHours(date.getHours() - 6);
  return newDate;
};

export const getUniqueObjectsByProperty = (objArr: Array<any>, property: string) => {
  const distinct = (value: any, index: number, self: any) => {
    return self.findIndex((obj: any) => obj[property] === value[property]) === index;
  };
  const uniqueObjArr = objArr.filter(distinct);
  return uniqueObjArr;
};

export const convertRawData = (rawData: Array<any>) => {
  let resObj: any = {};
  resObj = rawData.reduce((acc: any, current: { identifier: any; data: any[] }) => {
    return (acc = {
      ...acc,
      [current.identifier]: current.data.reduce((acc2: any, current2: any) => {
        const withoutMilliseconds = current2.key.split(".")[0] + "Z";
        //return acc2 = {...acc2, [current2.key]: current2.value };
        return (acc2 = { ...acc2, [withoutMilliseconds]: current2.value });
      }, {}),
    });
  }, {});
  return resObj;
};

export const removeItem = (arr: Array<any>, value: any): Array<any> => {
  const index = arr.indexOf(value);
  if (index > -1) {
    arr.splice(index, 1);
  }
  return arr;
};

export const removeItemById = ([...arr]: Array<any>, id: string): Array<any> => {
  const element = arr.find((a) => a.identifier === id);
  const index = arr.indexOf(element);
  if (index > -1) {
    arr.splice(index, 1);
  }
  return arr;
};

export const getAllCategories = (arr: Array<any>): Array<string> => {
  const uniqueArr = getUniqueObjectsByProperty([...arr], "group");
  const groupArr = uniqueArr.map((u) => u.group);
  return groupArr;
};

let keys: any = { id: "key", title: "label", children: "children" };
export const renameNestedObject = (value: any): any => {
  if (!value || typeof value !== "object") return value;
  if (Array.isArray(value)) return value.map(renameNestedObject);
  return Object.fromEntries(Object.entries(value).map(([k, v]) => [keys[k] || k, renameNestedObject(v)]));
};

export const searchTree = (element: any, matchingKey: number): any => {
  if (element.key === matchingKey) {
    return { key: element.key, label: element.label };
  } else if (element.children != null) {
    let result = null;
    for (let i = 0; result === null && i < element.children.length; i++) {
      result = searchTree(element.children[i], matchingKey);
    }
    if (result !== null) {
      result = { key: result.key, label: result.label };
    }
    return result;
  }
  return null;
};

export const getObjectKey = (object: any) => {
  const keyArr = [];
  for (const [key, value] of Object.entries(object)) {
    keyArr.push(key);
  }
  return keyArr;
};

export const getChecked = (object: any) => {
  const keyArr = [];
  for (const [key, value] of Object.entries(object)) {
    if ((value as any).checked === true) {
      keyArr.push(parseInt(key, 10));
    }
  }
  return keyArr;
};

export const isDate2BeforeDate1 = (date1: Date | undefined, date2: Date | undefined) => {
  if (date1 !== undefined && date2 !== undefined) {
    if (date2 < date1) {
      return true;
    }
  }
};

export const removeElement = (id: string) => {
  const node = document.getElementById(id);
  if (node !== null && node.parentNode !== null) {
    return node.parentNode.removeChild(node);
  }
};

export const getUniqueFlatArr = (arr: Array<any>) => {
  const cleanArr = arr.flat();
  let flatArray = cleanArr.reduce((acc: Array<any>, current) => {
    if (!acc.includes(current) && isColorDarkEnough(current)) acc.push(current);
    return acc;
  }, []);
  return flatArray;
};

export const isColorDarkEnough = (color: string) => {
  color = color.substring(1); // strip #
  const rgb = parseInt(color, 16); // convert rrggbb to decimal
  const r = (rgb >> 16) & 0xff; // extract red
  const g = (rgb >> 8) & 0xff; // extract green
  const b = (rgb >> 0) & 0xff; // extract blue

  const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b; // per ITU-R BT.709

  if (luma < 200) {
    return true;
  } else return false;
};

export const createIStatsRows = (data: Array<IDataSet>): Array<IStatsRow> => {
  const statsArr = data.map((d: IDataSet) => {
    const valueArr: number[] = convertValue(d.data);
    const min: number = round2decimals(Math.min(...valueArr));
    const max: number = round2decimals(Math.max(...valueArr));

    const statsRow: IStatsRow = {
      title: d.title.replace(/_/g, " ").toUpperCase(),
      unit: d.unit,
      pointCount: d.data.length,
      min: min,
      max: max,
      delta: round2decimals(max - min),
      mean: round2decimals(valueArr.reduce((a: any, b: any) => a + b, 0) / valueArr.length),
    };
    return statsRow;
  });
  return statsArr;
};

export const createPieData = (dArr: Array<IDataSet>, aggregationMethod: AggregationType) => {
  const plotData: { values: Array<number>; labels: Array<string> } = { values: [], labels: [] };
  dArr.forEach((d) => {
    let values: number = 0;
    const sumValues = convertValue(d.data).reduce((acc: number, curr: number) => acc + curr, 0);
    switch (aggregationMethod) {
      case "AVG":
        values = round2decimals(sumValues / d.data.length);
        break;
      case "SUM":
        values = round2decimals(sumValues);
        break;
      default:
        values = round2decimals(sumValues);
        break;
    }
    plotData.values.push(values);
    plotData.labels.push(`${d.title.replace(/_/g, " ").toUpperCase()}, ${d.unit}`);
  });
  return plotData;
};
